import React from "react";
import { useParams, Navigate, Link } from "@/i18n/router";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight, ArrowRight, AlertTriangle, Workflow, Layers } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { SectionHeading } from "@/components/ui/primitives";
import { getSolution, solutions, solutionArchitecture } from "@/data/solutions";
import { useLang } from "@/i18n/LanguageContext";
import { deepLocalize } from "@/i18n/translate";
import { PLATFORM_URL } from "@/lib/api";

export default function SolutionDetail() {
  const { slug } = useParams();
  const { lang, tr } = useLang();
  const raw = getSolution(slug);
  if (!raw) return <Navigate to="/solutions" replace />;
  const solution = deepLocalize(raw, lang);
  const others = deepLocalize(solutions.filter((s) => s.slug !== slug).slice(0, 3), lang);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `AI Governance for ${solution.name}`,
    provider: { "@type": "Organization", name: "AstroLife" },
    description: solution.hero,
  };

  return (
    <>
      <Seo
        title={`${solution.name} — AI Governance Solution`}
        description={solution.hero}
        path={`/solutions/${solution.slug}`}
        image={solution.image}
        jsonLd={jsonLd}
      />

      <PageHero
        overline={solution.tagline}
        title={`${tr("AI Governance for")} ${solution.name}`}
        subtitle={solution.hero}
        breadcrumbs={[{ label: tr("Solutions"), to: "/solutions" }, { label: solution.name }]}
      />

      <section className="bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-lg md:text-xl leading-relaxed text-silver/80">{solution.intro}</p>
              </Reveal>

              <Reveal delay={0.05}>
                <h2 className="mt-12 font-heading text-2xl font-bold text-white">{tr("Outcomes")}</h2>
                <ul className="mt-6 space-y-4">
                  {solution.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-astro-light" />
                      <span className="text-base leading-relaxed text-silver/75">{o}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <div className="overflow-hidden rounded-2xl border border-navy-border">
                  <img src={solution.image} alt={solution.name} loading="lazy" className="h-64 w-full object-cover" />
                </div>
                <div className="mt-6 rounded-2xl border border-navy-border bg-navy-surface p-7">
                  <h3 className="font-heading text-lg font-semibold text-white">{tr("Relevant capabilities")}</h3>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {solution.capabilities.map((c) => (
                      <span key={c} className="rounded-full border border-astro-blue/30 bg-astro-blue/10 px-3 py-1.5 text-xs font-medium text-astro-light">
                        {c}
                      </span>
                    ))}
                  </div>
                  <a
                    href={PLATFORM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-astro-blue px-6 py-3.5 font-semibold text-white hover:bg-astro-light transition-colors"
                    data-testid="solution-launch-platform"
                  >
                    {tr("Launch Platform")} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Challenges + Use Cases + Architecture */}
      <section className="border-t border-navy-border bg-navy-surface py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-6 w-6 text-astro-light" />
                <h2 className="font-heading text-2xl font-bold text-white">{tr("Challenges")}</h2>
              </div>
              <ul className="mt-6 space-y-3">
                {(solution.challenges || []).map((c) => (
                  <li key={c} className="rounded-xl border border-navy-border bg-navy p-4 text-sm text-silver/75">{c}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="flex items-center gap-3">
                <Workflow className="h-6 w-6 text-astro-light" />
                <h2 className="font-heading text-2xl font-bold text-white">{tr("Representative Use Cases")}</h2>
              </div>
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {(solution.useCases || []).map((u) => (
                  <li key={u} className="rounded-xl border border-navy-border bg-navy p-4 text-sm text-silver/75">{u}</li>
                ))}
              </ul>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 rounded-2xl border border-navy-border bg-navy p-8">
              <div className="flex items-center gap-3">
                <Layers className="h-6 w-6 text-astro-light" />
                <h2 className="font-heading text-2xl font-bold text-white">{tr("Reference Architecture")}</h2>
              </div>
              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {deepLocalize(solutionArchitecture, lang).map((a, i) => (
                  <div key={a} className="flex items-start gap-3">
                    <span className="font-heading text-lg font-black text-outline">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm leading-relaxed text-silver/75">{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Other solutions */}
      <section className="border-t border-navy-border bg-navy py-24 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <SectionHeading overline={tr("Explore")} title={tr("Other strategic domains")} />
          <Stagger className="mt-12 grid gap-6 sm:grid-cols-3">
            {others.map((s) => (
              <motion.div key={s.slug} variants={staggerItem}>
                <Link to={`/solutions/${s.slug}`} className="group flex h-full flex-col rounded-2xl border border-navy-border bg-navy p-6 transition-all hover:-translate-y-1 hover:border-astro-blue/40">
                  <s.icon className="h-7 w-7 text-astro-light" />
                  <h3 className="mt-5 font-heading text-lg font-semibold text-white">{s.name}</h3>
                  <p className="mt-2 flex-1 text-sm text-silver/60">{s.hero}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-astro-light">
                    {tr("Explore")} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <PlatformCta />
    </>
  );
}
